import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import surveySparrowApi from '@lib/surveySparrow/surveySparrowApi';

import { toastError } from '@/components/Toast';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import { NPS_SCORES } from '@/lib/surveySparrow/helper';
import { getSurveySettings, getUserInfo, printApiError } from '@/utils/Util';
import {
	setIsCommonSurveyPopupOpen,
	setIsHighlightFeedback,
	setIsTrendSurveyPopupOpen
} from '@/store/surveySparrow/actions';
import { setSurveySettings } from '@/services/commonService';
import CommonImage from '@/components/CommonImage';

const CommonSurveyPopup = ({ surveyId, surveyTypeDate, popupName }) => {
	const [npsScore, setNpsScore] = useState(null);
	const npsScoreRef = useRef(null);
	const [feedback, setFeedback] = useState('');
	const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
	const [responseId, setResponseId] = useState(null);
	const [questionList, setQuestionList] = useState([]);
	const [contactList, setContactList] = useState([]);
	const dispatch = useDispatch();

	const getQuestionList = async () => {
		try {
			const response = await surveySparrowApi.get(`/questions?survey_id=${surveyId}`);
			setQuestionList(response.data);
		} catch (error) {
			printApiError(error);
		}
	};

	const getContactList = async () => {
		try {
			const response = await surveySparrowApi.get(`/contacts?search=${getUserInfo().email}`);
			setContactList(response.data);
		} catch (error) {
			printApiError(error);
		}
	};

	useEffect(() => {
		if (isFeedbackSubmitted) {
			dispatch(setIsHighlightFeedback(true));
		}
	}, [isFeedbackSubmitted]);

	useEffect(() => {
		getQuestionList();
		getContactList();
	}, []);

	const submitAnswer = async (answer, questionId) => {
		try {
			dispatch(openLoader());
			const requestBody = {
				survey_id: surveyId,
				contact_id: contactList[0]?.id,
				answers: [
					{
						answer,
						question_id: questionId
					}
				]
			};

			const response = responseId
				? await surveySparrowApi.put(`/responses/${responseId}`, requestBody)
				: await surveySparrowApi.post('/responses', requestBody);
			setResponseId(response.data.id);
			dispatch(closeLoader());
			return response;
		} catch {
			toastError('Something went wrong');
			dispatch(closeLoader());
		}
	};

	const handleNpsClick = async (score) => {
		const surveySettings = getSurveySettings();
		const surveyDate = surveySettings?.[surveyTypeDate];
		const currentDate = new Date();
		const surveyDateObj = new Date(surveyDate);
		const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
		const isNextMonthSame =
			nextMonth.getFullYear() === surveyDateObj.getFullYear() &&
			nextMonth.getMonth() === surveyDateObj.getMonth();
		if (!isNextMonthSame) {
			const response = await submitAnswer(score, questionList[0]?.id);
			if (response.data.id) {
				setNpsScore(score);
				npsScoreRef.current = score;
				setResponseId(response.data.id);

				const currentDate = new Date();
				const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 5);
				const nextSurveyDate = nextMonth.toISOString();

				await setSurveySettings(surveyTypeDate, nextSurveyDate);
				if (score > 7) {
					setIsFeedbackSubmitted(true);
					setTimeout(onClosePopup, 2000);
				}
			}
		} else {
			if (popupName === 'commonSurveyPopup') {
				dispatch(setIsCommonSurveyPopupOpen(false));
			} else if (popupName === 'trendSurveyPopup') {
				dispatch(setIsTrendSurveyPopupOpen(false));
			}
		}
	};

	const handleSubmit = async () => {
		const response = await submitAnswer(feedback, questionList[1]?.id);
		if (response.data.id) {
			setIsFeedbackSubmitted(true);
			setTimeout(onClosePopup, 2000);
		}
	};

	const onClosePopup = async () => {
		if (npsScoreRef.current === null) {
			const surveySettings = getSurveySettings();
			const surveyDate = surveySettings?.[surveyTypeDate];
			if (surveyDate) {
				const currentDate = new Date();
				const surveyDateObj = new Date(surveyDate);
				const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
				const isNextMonthSame =
					nextMonth.getFullYear() === surveyDateObj.getFullYear() &&
					nextMonth.getMonth() === surveyDateObj.getMonth();

				if (!isNextMonthSame) {
					await setSurveySettings(
						surveyTypeDate,
						new Date(new Date().setDate(new Date().getDate() + 1)).toISOString()
					);
				}
			} else {
				await setSurveySettings(
					surveyTypeDate,
					new Date(new Date().setDate(new Date().getDate() + 1)).toISOString()
				);
			}
		}
		if (popupName === 'commonSurveyPopup') {
			dispatch(setIsCommonSurveyPopupOpen(false));
		} else if (popupName === 'trendSurveyPopup') {
			dispatch(setIsTrendSurveyPopupOpen(false));
		}
	};

	const renderNpsRating = () => (
		<div className="nps-rating-container">
			<h4 className="semibold-16 mb-3">{questionList?.[0]?.rtxt}</h4>
			<div className="nps-rating-items">
				{NPS_SCORES.map((score) => (
					<div
						key={score}
						className={`nps-rating-item cursor-pointer ${npsScore === score ? 'active' : ''}`}
						onClick={() => handleNpsClick(score)}
					>
						{score}
					</div>
				))}
			</div>
			<div className="nps-rating-hint d-flex justify-content-between mt-1">
				<p className="regular-12 gray_300">Least Likely</p>
				<p className="regular-12 gray_300">Most Likely</p>
			</div>
		</div>
	);

	const renderFeedbackForm = () => (
		<div className="nps-feedback-container">
			<div className="form-group">
				<label className="semibold-16 mb-3">{questionList[1]?.rtxt}</label>
				<textarea
					placeholder="Write in details"
					className="h-20"
					value={feedback}
					onChange={(e) => setFeedback(e.target.value)}
					autoFocus
				/>
			</div>
		</div>
	);

	const renderSuccessMessage = () => (
		<div className="feedback-submitted text-center py-4 my-4">
			<p className="semibold-16 mb-1">Thanks for your valuable feedback!</p>
			<p className="regular-12 gray_dark_02">We will use your feedback to improve our service.</p>
		</div>
	);

	return (
		questionList?.length > 0 && (
			<div className="survey-sparrow-popup">
				<div className="close-btn cursor-pointer" onClick={onClosePopup}>
					<CommonImage src="/icons/close.svg" />
				</div>
				<div className="common-popup-body">
					<div className="question">
						{(npsScore === null || npsScore > 7) && !isFeedbackSubmitted && renderNpsRating()}

						{npsScore <= 7 && npsScore !== null && !isFeedbackSubmitted && renderFeedbackForm()}

						{isFeedbackSubmitted && renderSuccessMessage()}
					</div>
				</div>
				<div className="common-popup-footer text-right">
					{npsScore <= 7 && npsScore !== null && !isFeedbackSubmitted && (
						<button
							className="button primary size32 mr-3"
							onClick={handleSubmit}
							disabled={!feedback.trim()}
						>
							<span>Submit</span>
						</button>
					)}
				</div>
			</div>
		)
	);
};

export default CommonSurveyPopup;
